import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { useS3Upload, getS3SignedUrl } from "../../../utils/s3Client";
import { useFlashMessage } from "../../../components/flashMessage/FlashMessageContext";
import { useRiskAssessment, usePremisesStore } from "../../../store/store";
import { MultiSelectDropdown } from "../../../components/dropdown/MultiSelectDropdown";
import {
  initialPremisesObject,
  initialRiskAssObject,
  assessmentInitalKeys,
  OrgUserType,
} from "../../../types/api";
import {
  isDefined,
  isRegexValidate,
  deepEqual,
  filterObjectByKeys,
} from "../../../common/utils";
import { Trash2Icon } from "lucide-react";
import { message } from "antd";

// Extend the initialRiskAssObject type to support arrays
interface ExtendedRiskAssObject
  extends Omit<
    initialRiskAssObject,
    "assessor" | "assessorEmailAddress" | "premises"
  > {
  premises: string[];
  assessor: string[];
  assessorEmailAddress: string[];
  location?: string;
  likelihood?: string;
  [key: string]: any;
}

interface IRiskAssessmentProps {
  sendErrorMessage: (msg: string) => void;
  sendLoader: (loader: boolean) => void;
  movenext: (level: number) => void;
  assessmentId?: string;
  isReadOnly?: boolean;
  sendSelectedPremise?: (premiseIds: string[]) => void;
  premiseUsers: OrgUserType[];
  moduleName?: string;
}

interface formHandle {
  createRiskForm: () => void;
}

const RiskAssesmentForm = forwardRef<formHandle, IRiskAssessmentProps>(
  (
    {
      sendErrorMessage,
      sendLoader,
      movenext,
      assessmentId,
      isReadOnly = false,
      sendSelectedPremise,
      premiseUsers,
      moduleName,
    },
    ref
  ) => {
    const {
      riskAssessment,
      successMssage,
      createRiskAssessment,
      fetchRiskAssessmentById,
      updateRiskAssessment,
      getSuccessMessage,
    } = useRiskAssessment();
    const {
      allPremises: rawPremises,
      fetchPremises,
    }: { allPremises: any[]; fetchPremises: () => void } = usePremisesStore();

    const allPremises: initialPremisesObject[] = rawPremises.map(
      (premises) => ({
        id: premises.id,
        premisesName: premises.premisesName,
        addressLine1: premises.addressLine1,
        city: premises.townCity,
        responsiblePerson: premises.responsiblePerson,
      })
    );

    useEffect(() => {
      fetchPremises();
    }, []);
    const { uploadFileToS3, progress, uploading, error } = useS3Upload();
    const [riskObject, setRiskObject] = useState<ExtendedRiskAssObject | null>(
      () => {
        if (!riskAssessment) return null;

        // Ensure assessor and assessorEmailAddress are arrays
        const assessor = Array.isArray(riskAssessment.assessor)
          ? riskAssessment.assessor
          : [];

        const assessorEmailAddress = Array.isArray(
          riskAssessment.assessorEmailAddress
        )
          ? riskAssessment.assessorEmailAddress
          : [];

        const premises = Array.isArray(riskAssessment.premises)
          ? riskAssessment.premises
          : [];

        return {
          ...riskAssessment,
          premises,
          assessor,
          assessorEmailAddress,
          responsiblePerson: Array.isArray(
            (riskAssessment as any).responsiblePerson
          )
            ? (riskAssessment as any).responsiblePerson
            : typeof (riskAssessment as any).responsiblePerson === "string" &&
              (riskAssessment as any).responsiblePerson
              ? [(riskAssessment as any).responsiblePerson]
              : [],
        };
      }
    );
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [isSubmit, setIsSubmit] = useState<boolean>(false);
    const { addMessage } = useFlashMessage();
    const containerRef = useRef<HTMLDivElement | null>(null);

    const updateRiskObject = (
      event:
        | React.ChangeEvent<HTMLSelectElement>
        | React.ChangeEvent<HTMLInputElement>
        | { target: { name: string; value: string[] | string } }
    ) => {
      setIsSubmit(false);
      let local_riskObject =
        riskObject !== null ? JSON.parse(JSON.stringify(riskObject)) : {};
      const { name, value } = event.target;

      local_riskObject[name] = value;

      if (name === "premises") {
        const selectedIds = Array.isArray(value) ? value : [value];
        const selectedPremisesList = allPremises.filter((e) =>
          selectedIds.includes(e.id ?? "")
        );
        // Aggregate responsible persons from all selected premises
        const responsiblePersons = Array.from(
          new Set(
            selectedPremisesList
              .map((p) => p.responsiblePerson)
              .filter((v): v is string => Boolean(v))
          )
        );
        // If exactly one selected premise, set address; else clear address
        if (selectedIds.length === 1 && selectedPremisesList.length === 1) {
          local_riskObject.premisesName = selectedIds[0];
          local_riskObject.addressLine1 =
            selectedPremisesList[0].addressLine1 ?? "";
        } else {
          local_riskObject.premisesName = "";
          local_riskObject.addressLine1 = "";
        }
        local_riskObject.responsiblePerson = responsiblePersons;
        local_riskObject = {
          ...local_riskObject,
          assessor: [],
          assessorEmailAddress: [],
        };

        if (sendSelectedPremise) {
          sendSelectedPremise(selectedIds.filter(Boolean) as string[]);
        }
      }

      if (name === "assessor") {
        // When assessors are selected, automatically update their email addresses
        const selectedUsers = premiseUsers.filter((user) =>
          (value as string[]).includes(user._id as string)
        );
        local_riskObject.assessorEmailAddress = selectedUsers.map(
          (user) => user.email
        );
        // Do not modify responsiblePerson here; it's derived from premises
      }

      setRiskObject(local_riskObject);
    };

    // Function to convert riskObject to API format
    const convertToApiFormat = (
      obj: ExtendedRiskAssObject
    ): initialRiskAssObject => {
      return {
        ...obj,
        premises: Array.isArray(obj.premises) ? obj.premises : [],
        assessor: Array.isArray(obj.assessor) ? obj.assessor : [],
        assessorEmailAddress: Array.isArray(obj.assessorEmailAddress)
          ? obj.assessorEmailAddress
          : [],
      } as initialRiskAssObject;
    };

    useImperativeHandle(ref, () => ({
      createRiskForm: async () => {
        setIsSubmit(true);
        if (
          riskObject == null ||
          !Array.isArray(riskObject?.premises) ||
          !riskObject?.premises?.length ||
          !(
            Array.isArray(riskObject?.responsiblePerson) &&
            riskObject.responsiblePerson.length > 0
          ) ||
          !riskObject?.assessor?.length ||
          !riskObject?.assessorEmailAddress?.length ||
          (file !== null && !isDefined(riskObject.ImageOfPremises))
        ) {
          sendErrorMessage("Please fill all required fields!!");
          return;
        }

        // Validate all email addresses
        const allEmailsValid = riskObject.assessorEmailAddress.every((email) =>
          isRegexValidate(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, email)
        );

        if (!allEmailsValid) {
          sendErrorMessage("Invalid email address format!");
          return;
        } else if (
          deepEqual(riskObject, riskAssessment) &&
          riskObject !== null
        ) {
          movenext(1);
        } else if (
          !deepEqual(riskObject, riskAssessment) &&
          riskObject !== null &&
          isDefined(riskObject._id)
        ) {
          try {
            const filtered = filterObjectByKeys(
              riskObject,
              assessmentInitalKeys
            );
            sendErrorMessage("");
            await updateRiskAssessment(
              convertToApiFormat(filtered),
              moduleName
            );
            const msg = getSuccessMessage();
            if (isDefined(msg)) {
              movenext(1);
            }
          } catch (err: any) { }
        } else {
          try {
            sendErrorMessage("");
            await createRiskAssessment(
              convertToApiFormat(riskObject),
              moduleName
            );
            const msg = getSuccessMessage();
            if (isDefined(msg)) {
              movenext(1);
            }
          } catch (err: any) { }
        }
      },
    }));

    const handleFileChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (isReadOnly) {
        e.target.value = "";
        return;
      }
      const file = e.target.files?.[0];
      if (file && file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setImageSrc(url);
        setFile(file);
      } else {
        setImageSrc(null);
        setFile(null);
        alert("Please select a valid image file.");
      }
    };
    /*
        const uploadtos3 = async (file: File | null) => {
          if (!file) {
            sendErrorMessage("File cannot be empty");
            return;
          }
          sendLoader(true);
          try {
            // uploading to S3 Bucket
            const uploadedFileUrl = await uploadFileToS3(file);
            const local_riskObject = JSON.parse(JSON.stringify(riskObject));
            local_riskObject.ImageOfPremises = uploadedFileUrl;
            setRiskObject(local_riskObject);
            addMessage("Image uploaded successfully!", "success");
          } catch (err: any) {
            sendErrorMessage(err?.message ?? "Something went wrong");
          } finally {
            sendLoader(false);
          }
        };
    */
    const uploadtos3 = async (file: File | null) => {
      if (!file) {
        sendErrorMessage("File cannot be empty");
        return;
      }

      const premiseId = riskObject && Array.isArray(riskObject.premises)
        ? riskObject.premises[0]
        : "";

      if (!premiseId) {
        sendErrorMessage("Please select a premise before uploading the image");
        return;
      }

      sendLoader(true);

      try {
        const uploadedFileKey = await uploadFileToS3(file, { premiseId, });
        const local_riskObject = JSON.parse(JSON.stringify(riskObject));
        local_riskObject.ImageOfPremises = uploadedFileKey;
        setRiskObject(local_riskObject);

        addMessage("Image uploaded successfully!", "success");
      } catch (err: any) {
        sendErrorMessage(err?.message ?? "Something went wrong");
      } finally {
        sendLoader(false);
      }
    };

    const fetchImageUrl = async (key: string) => {
      try {
        const name = key.split("/").pop() || key;
        const extension = name.split(".").pop() || "";
        const fileurl = await getS3SignedUrl(key, extension);
        return fileurl;
      } catch (err: any) {
        console.error("Error fetching signed image URL:", err);
        return null;
      }
    };

    useEffect(() => {
      if (
        riskAssessment !== null &&
        riskAssessment.ImageOfPremises
      ) {
        const imageKey = riskAssessment.ImageOfPremises;

        fetchImageUrl(imageKey)
          .then((res: any) => {
            setImageSrc(res || imageKey);
          })
          .catch(() => {
            setImageSrc(imageKey);
          });
      }
    }, []);

    const getRiskAssessment = async () => {
      await fetchRiskAssessmentById(assessmentId ?? "");
    };
    useEffect(() => {
      if (isDefined(assessmentId)) {
        getRiskAssessment();
      }
    }, [assessmentId]);
    useEffect(() => {
      if (riskAssessment.ImageOfPremises) {
        const imageKey = riskAssessment.ImageOfPremises;

        fetchImageUrl(imageKey)
          .then((res: any) => {
            setImageSrc(res || imageKey);
          })
          .catch(() => {
            setImageSrc(imageKey);
          });
      }
      if (riskAssessment?.premises && sendSelectedPremise) {
        const selectedIds = Array.isArray(riskAssessment.premises)
          ? riskAssessment.premises
          : [];
        sendSelectedPremise(selectedIds);
      }

      setRiskObject({
        ...riskAssessment,
        premises: Array.isArray(riskAssessment.premises)
          ? riskAssessment.premises
          : [],
        assessor: Array.isArray(riskAssessment.assessor)
          ? riskAssessment.assessor
          : [],
        assessorEmailAddress: Array.isArray(riskAssessment.assessorEmailAddress)
          ? riskAssessment.assessorEmailAddress
          : [],
      } as ExtendedRiskAssObject);
    }, [riskAssessment]);

    useEffect(() => {
      if (
        isReadOnly === true ||
        (isDefined(riskAssessment?.status) && riskAssessment?.status != "open")
      ) {
        const container = containerRef.current;
        if (container) {
          const elements = container.querySelectorAll<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
          >("input, textarea, select");

          elements.forEach((el) => {
            if (
              el instanceof HTMLInputElement ||
              el instanceof HTMLTextAreaElement
            ) {
              el.readOnly = true;
            } else if (el instanceof HTMLSelectElement) {
              el.disabled = true;
            }
          });
        }
      }
    }, [isReadOnly, riskAssessment]);
    return (
      <React.Fragment>
        <div
          ref={containerRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-8 p-6"
        >
          {/* Premises */}
          <div className="w-full flex flex-col">
            <label className="block text-[13px] font-semibold text-[#16202b] mb-2">
              Premises
            </label>
            <div
              className={`rounded-lg ${isSubmit && !riskObject?.premises?.length
                ? "border-red-500 border-2"
                : "border border-[#e3e8ec]"
                }`}
            >
              <MultiSelectDropdown
                options={allPremises.map((item: any) => ({
                  label: item.premisesName,
                  value: item.id,
                }))}
                selectedValues={
                  Array.isArray(riskObject?.premises) && riskObject
                    ? riskObject.premises
                    : []
                }
                onChange={(selectedValues) => {
                  if (isReadOnly) return;

                  updateRiskObject({
                    target: {
                      name: "premises",
                      value: selectedValues,
                    },
                  });
                }}
                placeholder="Select Premises"
                containerClassName={`bg-white rounded-lg ${isReadOnly ? "pointer-events-none opacity-70" : ""
                  }`}
              />
            </div>
          </div>

          {/* Responsible Person / Duty Holder */}
          <div className="w-full flex flex-col">
            <label className="block text-[13px] font-semibold text-[#16202b] mb-2">
              Responsible Person / Duty Holder
            </label>
            <div
              className={`w-full rounded-lg p-3 border border-[#e3e8ec] bg-[#f7f9fa] min-h-[42px] max-h-[85px] overflow-y-auto flex flex-col justify-center gap-1.5 ${isSubmit && !riskObject?.premises?.length
                ? "border-red-500 border-2"
                : ""
                }`}
            >
              {Array.isArray(riskObject?.premises) &&
                riskObject &&
                riskObject.premises.length > 0 ? (
                (() => {
                  const selectedPremisesList = allPremises.filter((p) =>
                    (riskObject.premises as string[]).includes(p.id ?? "")
                  );
                  const persons = Array.from(
                    new Set(
                      selectedPremisesList
                        .map((p) => p.responsiblePerson)
                        .filter((v): v is string => Boolean(v))
                    )
                  );
                  return persons.length ? (
                    persons.map((name, idx) => (
                      <div
                        key={idx}
                        className="text-sm font-semibold text-[#43525f] flex items-center gap-2"
                      >
                        <span className="w-2 h-2 rounded-full bg-[#0e9f8e] shrink-0"></span>
                        {name}
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center gap-2 text-[#7b8a97] text-sm">
                      <span className="w-2 h-2 rounded-full bg-[#d7dee4] shrink-0"></span>
                      No responsible person found
                    </div>
                  );
                })()
              ) : (
                <div className="flex items-center gap-2 text-[#7b8a97] text-sm">
                  <span className="w-2 h-2 rounded-full bg-[#d7dee4] shrink-0"></span>
                  Select premises first
                </div>
              )}
            </div>
          </div>

          {/* Address of Premises */}
          <div className="w-full flex flex-col">
            <label className="block text-[13px] font-semibold text-[#16202b] mb-2">
              Address of premises
            </label>
            <textarea
              rows={2}
              className="w-full border border-[#e3e8ec] rounded-lg p-2.5 bg-[#f7f9fa] text-[#43525f] text-sm resize-none outline-none font-medium"
              disabled
              value={riskObject?.addressLine1 || ""}
            />
          </div>

          {/* Assessors */}
          <div className="w-full flex flex-col">
            <label className="block text-[13px] font-semibold text-[#16202b] mb-2">
              Assessors
            </label>
            <div
              className={`rounded-lg ${isSubmit && !riskObject?.assessor?.length
                ? "border-red-500 border-2"
                : "border border-[#e3e8ec]"
                }`}
            >
              <MultiSelectDropdown
                options={(premiseUsers || []).map((user) => ({
                  label: `${user.firstName} ${user.lastName}`,
                  value: user._id as string,
                }))}
                selectedValues={
                  Array.isArray(riskObject?.assessor) && riskObject
                    ? riskObject.assessor
                    : []
                }
                onChange={(selectedValues) => {
                  if (isReadOnly) return;
                  updateRiskObject({
                    target: {
                      name: "assessor",
                      value: selectedValues,
                    },
                  });
                }}
                placeholder="Select Assessors"
                containerClassName={`bg-white rounded-lg ${isReadOnly ? "pointer-events-none opacity-70" : ""}`}
              />
            </div>
          </div>

          {/* Assessor Email Addresses */}
          <div className="w-full flex flex-col">
            <label className="block text-[13px] font-semibold text-[#16202b] mb-2">
              Assessor Email Addresses
            </label>
            <div className="w-full rounded-lg p-3 border border-[#e3e8ec] bg-[#f7f9fa] min-h-[42px] max-h-[85px] overflow-y-auto flex flex-col justify-center gap-1.5">
              {Array.isArray(riskObject?.assessorEmailAddress) &&
                riskObject &&
                riskObject.assessorEmailAddress.length > 0 ? (
                riskObject.assessorEmailAddress.map(
                  (email: string, index: number) => (
                    <div
                      key={index}
                      className="text-sm font-semibold text-[#43525f] flex items-center gap-2"
                    >
                      <span className="w-2 h-2 rounded-full bg-[#0e9f8e] shrink-0"></span>
                      {email}
                    </div>
                  )
                )
              ) : (
                <div className="flex items-center gap-2 text-[#7b8a97] text-sm">
                  <span className="w-2 h-2 rounded-full bg-[#d7dee4] shrink-0"></span>
                  Assessors' emails will appear here
                </div>
              )}
            </div>
          </div>

          {/* Image Preview / Browse */}
          <div className="w-full flex flex-col">
            <label className="block text-[13px] font-semibold text-[#16202b] mb-2">
              Image Of Premises
            </label>
            <div className="relative w-full flex items-center gap-4">
              <label
                className={`relative flex-1 h-[74px] rounded-lg border border-dashed ${isSubmit && file !== null && !isDefined(riskObject?.ImageOfPremises)
                  ? "border-red-500 bg-[#fbe9e9]"
                  : "border-[#c3ccd4] bg-[#f7f9fa]"
                  } ${isReadOnly
                    ? "cursor-default pointer-events-none opacity-70"
                    : "hover:border-[#0e9f8e] hover:text-[#0e9f8e] cursor-pointer"
                  } flex flex-col items-center justify-center text-center p-3 transition-colors`}
                htmlFor={isReadOnly ? undefined : "fileInput"}
              >
                {imageSrc || riskObject?.ImageOfPremises ? (
                  <img
                    src={imageSrc || riskObject?.ImageOfPremises}
                    alt="Preview"
                    className="max-h-[60px] max-w-full rounded object-contain"
                  />
                ) : isReadOnly ? (
                  <div className="text-[12.5px] leading-tight text-[#7b8a97] font-medium">
                    No image available
                  </div>
                ) : (
                  <div className="text-[12.5px] leading-tight text-[#43525f] font-medium">
                    Browse Image
                    <span className="block text-[11px] text-[#7b8a97] font-normal mt-0.5">
                      (optional)
                    </span>
                  </div>
                )}
              </label>
              <input
                type="file"
                className="hidden"
                id="fileInput"
                accept="image/*"
                disabled={isReadOnly}
                onChange={handleFileChange}
              />

              {/* Image control buttons */}
              {!isReadOnly && (file || imageSrc) && (
                <div className="flex flex-col gap-2 shrink-0">
                  {file && (
                    <button
                      type="button"
                      className="px-3 py-1 bg-[#0e9f8e] hover:bg-[#0b8577] text-white text-xs font-semibold rounded-full transition-colors shadow-sm"
                      onClick={() => uploadtos3(file)}
                    >
                      Upload
                    </button>
                  )}
                  <button
                    type="button"
                    className="px-3 py-1 bg-red-100 hover:bg-red-200 text-[#dc2626] text-xs font-semibold rounded-full transition-colors flex items-center gap-1.5 border border-[#f2c0c0]"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImageSrc(null);
                      setFile(null);
                      const tempRiskObject = JSON.parse(
                        JSON.stringify(riskObject)
                      );
                      tempRiskObject.ImageOfPremises = "";
                      setRiskObject(tempRiskObject);
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Module location / likelihood for emergency module */}
          {moduleName === "external-emergency-preparedness" && (
            <div className="w-full flex flex-col">
              <label className="block text-[13px] font-semibold text-[#16202b] mb-2">
                Location
              </label>
              <input
                onChange={(e) => {
                  updateRiskObject({
                    target: {
                      name: "location",
                      value: e.target.value,
                    },
                  });
                }}
                placeholder="Enter location"
                type="text"
                disabled={isReadOnly}
                className="w-full border border-[#e3e8ec] rounded-lg p-2.5 bg-white text-[#16202b] text-sm outline-none font-medium"
                value={riskObject?.location || ""}
              />
            </div>
          )}

          {moduleName === "external-emergency-preparedness" && (
            <div className="w-full flex flex-col">
              <label className="block text-[13px] font-semibold text-[#16202b] mb-2">
                Likelihood
              </label>
              <input
                onChange={(e) => {
                  updateRiskObject({
                    target: {
                      name: "likelihood",
                      value: e.target.value,
                    },
                  });
                }}
                value={riskObject?.likelihood || ""}
                placeholder="Enter likelihood"
                type="text"
                disabled={isReadOnly}
                className="w-full border border-[#e3e8ec] rounded-lg p-2.5 bg-white text-[#16202b] text-sm outline-none font-medium"
              />
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }
);

export default RiskAssesmentForm;
